import React, { useEffect, useState, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { FaMapMarkedAlt, FaSyncAlt, FaExclamationTriangle, FaVoteYea, FaInfoCircle, FaUpload } from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, TechTabs, TechInput, TechTextarea, TechButton,
  StatStrip, StatTile, StatLabel, StatValue, StatSub, StatusPill,
  TechChip, Empty, Loading, ErrorBox, borderSoft, mono, accent,
} from '../../styles/techComponents';
import { getOpportunity, setCircuitoBarrio, listBarriosElectoral, loadElectoral } from '../../services/electoralService';
import MapaElectoral from './MapaElectoral';

// Convierte "12.345" / "12,345" / "12345" a entero (o null).
function toNum(s) {
  const n = parseInt(String(s ?? '').replace(/[^\d]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

// Parsea el CSV/TSV pegado. 1ra fila = encabezados. Columnas fijas: circuito,
// barrio, seccion, electores, votos_total. Cualquier otra columna es una FUERZA
// política y su valor va a resultados{ENCABEZADO: votos}.
function parseCircuitosCsv(text) {
  const lines = String(text || '').trim().split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return { rows: [], error: 'Pegá los encabezados y al menos una fila de datos.' };
  const delim = lines[0].includes('\t') ? '\t' : (lines[0].includes(';') ? ';' : ',');
  const headers = lines[0].split(delim).map((h) => h.trim());
  const norm = headers.map((h) => h.toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(delim);
    const row = { resultados: {} };
    headers.forEach((h, idx) => {
      const key = norm[idx];
      const raw = (cells[idx] ?? '').trim();
      if (key === 'circuito') row.circuito = raw;
      else if (key === 'barrio') { if (raw) row.barrio = raw; }
      else if (key === 'seccion' || key === 'sección') { if (raw) row.seccion = raw; }
      else if (key === 'electores') row.electores = toNum(raw);
      else if (key === 'votos_total' || key === 'votos totales' || key === 'votos') row.votos_total = toNum(raw);
      else if (h) { const v = toNum(raw); if (v != null) row.resultados[h] = v; }
    });
    if (row.circuito) rows.push(row);
  }
  if (rows.length === 0) return { rows: [], error: 'No se detectó ninguna fila con "circuito".' };
  return { rows };
}

function ImportadorCircuitos({ onDone }) {
  const [eleccion, setEleccion] = useState('');
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function importar() {
    setMsg(null);
    if (!eleccion.trim()) return setMsg('Poné un nombre de elección (ej: "2023 general").');
    const { rows, error } = parseCircuitosCsv(csv);
    if (error) return setMsg(error);
    setBusy(true);
    try {
      const r = await loadElectoral({ eleccion: eleccion.trim(), rows });
      setMsg(`✅ ${r.cargados} circuito(s) cargado(s).`);
      setCsv('');
      if (onDone) onDone();
    } catch (e) {
      setMsg(e?.response?.status === 403 ? 'Necesitás permiso de administrador para cargar datos.' : friendlyError(e));
    } finally { setBusy(false); }
  }

  return (
    <TechSection title="Cargar resultados por circuito" icon={<FaUpload />}>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.84rem', marginTop: 0 }}>
        Pegá los resultados oficiales (uno por circuito). Primera fila = encabezados. Columnas fijas:
        {' '}<b>circuito, barrio, electores, votos_total</b>. Cualquier otra columna es una fuerza (ej: <b>LLA, HECHOS, UP</b>) y sus votos.
      </p>
      <div style={{ fontFamily: mono, fontSize: '0.74rem', color: 'var(--text-secondary)', border: `1px solid ${borderSoft}`, borderRadius: 8, padding: '0.6rem', marginBottom: '0.6rem', whiteSpace: 'pre', overflowX: 'auto' }}>
        circuito,barrio,electores,votos_total,LLA,HECHOS,UP{'\n'}000901,Centro,4200,3600,1500,1300,800{'\n'}000902,Alto Verde,3100,2600,700,1400,500
      </div>
      <div style={{ marginBottom: '0.5rem', maxWidth: 320 }}>
        <TechInput placeholder='Elección (ej: "2023 general")' value={eleccion} onChange={(e) => setEleccion(e.target.value)} />
      </div>
      <TechTextarea rows={7} placeholder="Pegá acá el CSV de circuitos…" value={csv} onChange={(e) => setCsv(e.target.value)} style={{ width: '100%', fontFamily: mono, fontSize: '0.82rem' }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: '0.5rem' }}>
        <TechButton onClick={importar} disabled={busy}><FaUpload /> {busy ? 'Cargando…' : 'Cargar circuitos'}</TechButton>
        {msg && <StatusPill>{msg}</StatusPill>}
      </div>
    </TechSection>
  );
}

const CAT_COLOR = {
  'competitivo · ganable': '#22c55e', 'competitivo · defender': '#00b4d8',
  'base a movilizar': '#8b5cf6', 'territorio rival': '#ef4444', 'sin dato': '#64748b',
};
const Bar = styled.div` height:8px;border-radius:4px;background:rgba(255,255,255,0.06);overflow:hidden;min-width:80px;
  > span{display:block;height:100%;border-radius:4px;background:${({ $c }) => $c || accent};width:${({ $w }) => $w}%} `;
const Row = styled.div` display:grid;grid-template-columns:1.4fr 1fr 1.2fr 0.8fr;gap:0.6rem;align-items:center;
  padding:0.55rem 0;border-bottom:1px solid ${borderSoft};font-size:0.85rem;
  .n{font-family:${mono};font-variant-numeric:tabular-nums} @media(max-width:760px){grid-template-columns:1fr} `;
const BInput = styled.input` background:rgba(255,255,255,0.04);border:1px solid ${borderSoft};border-radius:5px;
  color:var(--text-primary);padding:0.25rem 0.4rem;font-size:0.78rem;width:100%;max-width:140px;
  &:focus{outline:none;border-color:${accent}} `;

export default function Electoral() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('barrios');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allBarrios, setAllBarrios] = useState([]);
  const [barrioSel, setBarrioSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, bs] = await Promise.all([getOpportunity(), listBarriosElectoral().catch(() => [])]);
      setData(d); setAllBarrios(bs);
    } catch (e) { setError(friendlyError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function saveBarrio(circuito, barrio) {
    try { await setCircuitoBarrio(circuito, barrio); load(); } catch (e) { /* silencioso */ }
  }

  if (loading) return <TechShell><Loading>Cargando modelo…</Loading></TechShell>;
  if (error) return <TechShell><ErrorBox><FaExclamationTriangle /> {error}</ErrorBox></TechShell>;

  const head = (
    <TechPageHeader
      title="Inteligencia Electoral"
      dossier="PREDICTIVO"
      subtitle="Dónde están los votos ganables: modelo de oportunidad por circuito y barrio, cruzando resultados electorales con la gestión territorial."
      actions={<button onClick={load} style={{ background: 'none', border: `1px solid ${borderSoft}`, color: 'var(--text-secondary)', borderRadius: 6, padding: '0.4rem 0.7rem', cursor: 'pointer', fontFamily: mono, fontSize: '0.75rem' }}><FaSyncAlt /> Actualizar</button>}
    />
  );

  if (data?.sin_datos_electorales) {
    return (
      <TechShell>
        {head}
        <TechSection title="Modelo listo — falta cargar los datos por circuito" icon={<FaInfoCircle />}>
          <p style={{ color: 'var(--text-secondary)' }}>{data.mensaje}</p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
            El motor de oportunidad está desplegado. Apenas se carguen los resultados por circuito de San Nicolás
            (elecciones 2023/2025), esta pantalla muestra el mapa de votos ganables por barrio, la competitividad
            de cada circuito y dónde rinde más el ground game.
          </p>
        </TechSection>
        <ImportadorCircuitos onDone={load} />
      </TechShell>
    );
  }

  const circuitos = data?.circuitos || [];
  const porBarrio = data?.por_barrio || [];
  const totalElectores = circuitos.reduce((a, c) => a + (c.electores || 0), 0);
  const ganables = circuitos.filter((c) => c.categoria === 'competitivo · ganable').length;

  const list = tab === 'barrios' ? porBarrio : circuitos;

  return (
    <TechShell>
      {head}
      <StatStrip>
        <StatTile><StatLabel>Circuitos</StatLabel><StatValue>{circuitos.length}</StatValue><StatSub>{data.eleccion}</StatSub></StatTile>
        <StatTile><StatLabel>Padrón analizado</StatLabel><StatValue>{totalElectores.toLocaleString('es-AR')}</StatValue><StatSub>electores</StatSub></StatTile>
        <StatTile><StatLabel>Circuitos ganables</StatLabel><StatValue style={{ color: '#22c55e' }}>{ganables}</StatValue><StatSub>competitivos con espacio</StatSub></StatTile>
      </StatStrip>

      {data.resumen?.fuerzas?.length > 0 && (
        <TechSection title={`Resultado en San Nicolás · ${data.eleccion}`} icon={<FaVoteYea />}>
          {data.resumen.participacion != null && (
            <div style={{ fontFamily: mono, fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
              Participación {data.resumen.participacion}% · {data.resumen.votos_total.toLocaleString('es-AR')} votos sobre {data.resumen.electores.toLocaleString('es-AR')} electores
            </div>
          )}
          {data.resumen.fuerzas.map((f, i) => {
            const esLLA = /lla|libertad|chouhy/i.test(f.nombre);
            const col = esLLA ? '#22c55e' : (i === 0 ? '#ef4444' : accent);
            return (
              <div key={f.nombre} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 90px', gap: 10, alignItems: 'center', padding: '0.25rem 0' }}>
                <span style={{ fontSize: '0.84rem', fontWeight: esLLA ? 700 : 400, color: esLLA ? '#22c55e' : 'var(--text-primary)' }}>{f.nombre}</span>
                <Bar $w={f.pct} $c={col}><span /></Bar>
                <span className="n" style={{ fontFamily: mono, fontSize: '0.8rem', textAlign: 'right' }}>{f.pct}% · {f.votos.toLocaleString('es-AR')}</span>
              </div>
            );
          })}
        </TechSection>
      )}

      <TechSection title="Mapa de votos ganables — San Nicolás" icon={<FaMapMarkedAlt />}>
        <MapaElectoral barrios={allBarrios} porBarrio={porBarrio} resumen={data.resumen} onSelectBarrio={setBarrioSel} />
      </TechSection>

      {barrioSel && (
        <TechSection title={`Asignar circuito a "${barrioSel.nombre}"`} icon={<FaInfoCircle />}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>¿Qué circuito electoral cubre este barrio?</span>
            <select onChange={(e) => { const c = e.target.value; if (c) { saveBarrio(c, barrioSel.nombre); setBarrioSel(null); } }}
              style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${borderSoft}`, color: 'var(--text-primary)', borderRadius: 6, padding: '0.35rem 0.5rem', fontSize: '0.82rem' }}>
              <option value="">Elegí el circuito…</option>
              {circuitos.map((c) => <option key={c.circuito} value={c.circuito}>{c.circuito} — {c.categoria} ({c.lla_share}% LLA)</option>)}
            </select>
            <button onClick={() => setBarrioSel(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.8rem' }}>cancelar</button>
          </div>
        </TechSection>
      )}

      <TechTabs tabs={[{ id: 'barrios', label: 'Por barrio' }, { id: 'circuitos', label: 'Por circuito' }]} active={tab} onChange={setTab} />

      <TechSection title="Mapa de oportunidad (ordenado por votos ganables)" icon={<FaMapMarkedAlt />}>
        {list.length === 0 && <Empty>Sin datos.</Empty>}
        {list.map((r, i) => (
          <Row key={i}>
            <div><b>{tab === 'circuitos' ? r.circuito : (r.barrio || r.circuito)}</b></div>
            <div className="n">{(r.electores || 0).toLocaleString('es-AR')} electores</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Bar $w={r.oportunidad || 0} $c={CAT_COLOR[r.categoria] || accent}><span /></Bar>
              <span className="n">{r.oportunidad || 0}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {r.categoria ? <TechChip style={{ color: CAT_COLOR[r.categoria], borderColor: CAT_COLOR[r.categoria] }}>{r.categoria}</TechChip> : null}
              {tab === 'circuitos' && (
                <BInput list="barrios-dl" placeholder="barrio…" defaultValue={r.barrio || ''}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.barrio || '')) saveBarrio(r.circuito, v); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
              )}
            </div>
          </Row>
        ))}
        <datalist id="barrios-dl">{allBarrios.map((b) => <option key={b.id} value={b.nombre} />)}</datalist>
      </TechSection>
      {tab === 'circuitos' && (
        <p style={{ fontFamily: mono, fontSize: '0.72rem', color: accent, marginTop: '0.5rem' }}>
          ✏️ Escribí el barrio de cada circuito y tocá Enter — así se enciende el tab "Por barrio".
        </p>
      )}
      <p style={{ fontFamily: mono, fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.7rem' }}>
        <FaVoteYea /> Índice de oportunidad = competitividad × espacio de crecimiento de LLA, ponderado por padrón. Se afina con más datos por circuito y gestión.
      </p>
      <ImportadorCircuitos onDone={load} />
    </TechShell>
  );
}
